import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Container,
  Button,
  CircularProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Layout from '../../components/layout/Layout';
import gstReportService from '../../services/gstReportService';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { formatCurrency } from '../../utils/formatCurrency';
import * as XLSX from 'xlsx';

const GSTReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({
    rows: [],
    summary: null,
    taxRates: []
  });

  // Default to current month
  const getCurrentMonthDates = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date();
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const [startDate, setStartDate] = useState(getCurrentMonthDates().startDate);
  const [endDate, setEndDate] = useState(getCurrentMonthDates().endDate);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch (e) {
      return dateString;
    }
  };

  // Fetch report data
  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await gstReportService.getGSTReport(startDate, endDate);
      setReportData(response);
    } catch (error) {
      console.error('Error fetching GST report:', error);
      toast.error('Failed to load GSTR1 report');
    } finally {
      setLoading(false);
    }
  };

  // Load report on mount and when dates change
  useEffect(() => {
    fetchReportData();
  }, [startDate, endDate]);

  // Export to Excel
  const handleExport = async () => {
    try {
      setIsExporting(true);

      // Prepare data for export
      const exportData = reportData.rows.map(row => {
        const rowData = {
          'Date': formatDate(row.date),
          'Invoice No': row.invoiceNumber,
          'Customer': row.customerName,
          'Taxable Amount': row.taxableAmount || 0,
        };

        // Add dynamic tax columns (use tax name as key)
        reportData.taxRates.forEach(taxRate => {
          const taxName = taxRate.name;
          const taxAmount = row.taxAmounts && row.taxAmounts[taxName]
            ? row.taxAmounts[taxName]
            : 0;
          rowData[taxRate.name] = taxAmount;
        });

        rowData['Total GST'] = row.totalGST || 0;
        rowData['Total Amount'] = row.totalAmount || 0;

        return rowData;
      });

      // Add summary row
      if (reportData.summary) {
        const summaryRow = {
          'Date': 'TOTAL',
          'Invoice No': '',
          'Customer': '',
          'Taxable Amount': reportData.summary.totalTaxableAmount || 0,
        };

        reportData.taxRates.forEach(taxRate => {
          const taxName = taxRate.name;
          const taxAmount = reportData.summary.totalTaxAmounts && reportData.summary.totalTaxAmounts[taxName]
            ? reportData.summary.totalTaxAmounts[taxName]
            : 0;
          summaryRow[taxRate.name] = taxAmount;
        });

        summaryRow['Total GST'] = reportData.summary.totalGST || 0;
        summaryRow['Total Amount'] = reportData.summary.totalAmount || 0;

        exportData.push(summaryRow);
      }

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'GSTR1 Report');

      // Generate filename
      const filename = `GSTR1_Report_${startDate}_to_${endDate}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter rows by invoice number or customer name (single search field)
  const filteredRows = reportData.rows.filter(row => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const matchesInvoiceNo = row.invoiceNumber && row.invoiceNumber.toLowerCase().includes(query);
    const matchesCustomerName = row.customerName && row.customerName.toLowerCase().includes(query);
    return matchesInvoiceNo || matchesCustomerName;
  });

  // Paginated rows
  const paginatedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link component={RouterLink} to="/reports" color="inherit">
            Reports
          </Link>
          <Typography color="text.primary">GSTR1 Reports</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              GSTR1 Reports
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={isExporting || loading || reportData.rows.length === 0}
          >
            {isExporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search by Invoice No or Customer Name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter invoice number or customer name"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <Button
                  variant="contained"
                  onClick={fetchReportData}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Generate Report'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Report Table */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredRows.length === 0 ? (
              <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                {reportData.rows.length === 0
                  ? 'No data available for the selected date range'
                  : 'No records found matching your search criteria'}
              </Typography>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.main' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Invoice No</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer</TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                          Taxable Amount
                        </TableCell>
                        {/* Dynamic tax columns */}
                        {reportData.taxRates.map((taxRate) => (
                          <TableCell
                            key={taxRate.rate}
                            align="right"
                            sx={{ color: 'white', fontWeight: 'bold' }}
                          >
                            {taxRate.name}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                          Total GST
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                          Total Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRows.map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            '&:nth-of-type(odd)': { bgcolor: 'action.hover' }
                          }}
                        >
                          <TableCell>{formatDate(row.date)}</TableCell>
                          <TableCell>{row.invoiceNumber}</TableCell>
                          <TableCell>{row.customerName}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.taxableAmount)}
                          </TableCell>
                          {/* Dynamic tax columns */}
                          {reportData.taxRates.map((taxRate) => {
                            const taxName = taxRate.name;
                            const taxAmount = row.taxAmounts && row.taxAmounts[taxName]
                              ? row.taxAmounts[taxName]
                              : 0;
                            return (
                              <TableCell key={taxRate.name} align="right">
                                {taxAmount > 0 ? formatCurrency(taxAmount) : '-'}
                              </TableCell>
                            );
                          })}
                          <TableCell align="right">
                            {formatCurrency(row.totalGST)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(row.totalAmount)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Summary Row */}
                      {reportData.summary && (
                        <TableRow sx={{ bgcolor: 'primary.light', fontWeight: 'bold' }}>
                          <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>
                            <strong>TOTAL</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            <strong>{formatCurrency(reportData.summary.totalTaxableAmount)}</strong>
                          </TableCell>
                          {/* Dynamic tax columns summary */}
                          {reportData.taxRates.map((taxRate) => {
                            const taxName = taxRate.name;
                            const taxAmount = reportData.summary.totalTaxAmounts && reportData.summary.totalTaxAmounts[taxName]
                              ? reportData.summary.totalTaxAmounts[taxName]
                              : 0;
                            return (
                              <TableCell key={taxRate.name} align="right" sx={{ fontWeight: 'bold' }}>
                                <strong>{taxAmount > 0 ? formatCurrency(taxAmount) : '-'}</strong>
                              </TableCell>
                            );
                          })}
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            <strong>{formatCurrency(reportData.summary.totalGST)}</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            <strong>{formatCurrency(reportData.summary.totalAmount)}</strong>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filteredRows.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Layout>
  );
};

export default GSTReport;

